import { useState, useContext, useRef, useEffect, useMemo } from "react";
import Tooltip from "../../common/Tooltip.jsx";
import { DeccaContext } from "../../../StateManagment/DeccaContext.js";
import Decca from "./Decca.jsx";
import History from "./History.jsx";
import DeccaAvatar from "./DeccaAvatar.jsx";
import DeccaIcon from "./DeccaIcon.jsx";
import Location from "../../../hooks/Location.js";
import { db, timestamp } from "../../../firebase/config.js";
import uuid from "react-uuid";
import { client } from "../../../hooks/Client";
import { useFirestoreOrderBy } from "../../../firebase/useFirestore.js";
import { Auth } from "../../../StateManagment/Auth.js";
import newConversation from "../../../agents/newConversation.js";

const Sidebar = () => {
  // Context
  const {
    setChatOpen,
    selectedConversationID,
    setSelectedConversationID,
    setSubject,
    setContextData,
    setSelectedActivity,
    setSelectedEffect,
    entryPoint,
  } = useContext(DeccaContext);

  const [auth] = useContext(Auth);

  // State
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const stored = localStorage.getItem("decca:sidebarWidth");
      const num = stored ? parseInt(stored, 10) : NaN;
      return Number.isFinite(num) && num >= 240 ? num : 350;
    } catch {
      return 350;
    }
  });
  const [entered, setEntered] = useState(false);
  // 'chat' | 'history' — opened from the topbar lands on the "start a new
  // conversation" screen (chat view, no conversation); otherwise resume a
  // selected conversation, falling back to the history list.
  const [view, setView] = useState(
    selectedConversationID || entryPoint === "topbar" ? "chat" : "history"
  );

  // Location (hooks must always run)
  const [, loc, loc2] = Location();
  const currentPage = loc2;

  // Firestore hooks (must always run)
  const convsInternal = useFirestoreOrderBy("Conversations", "Position", "desc");

  const conversations = useMemo(
    // Hide soft-deleted conversations (ADR-008)
    () => (convsInternal || []).filter((c) => !c.Deleted),
    [convsInternal]
  );

  // Refs (must always run)
  const sidebarRef = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // Decide if sidebar should be hidden (NO early return before hooks)
  const hiddenPages = useMemo(
    () => ["login", "register", "livereportopen", "eventopen"],
    []
  );

  const isHiddenPage = hiddenPages.includes(loc) || hiddenPages.includes(loc2);

  // Slide-in on mount
  useEffect(() => {
    const t = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Selecting/creating a conversation always lands on the chat view
  useEffect(() => {
    if (selectedConversationID) setView("chat");
  }, [selectedConversationID]);

  // Resize logic
  const resize = (e) => {
    const deltaX = startX.current - e.clientX;
    const newWidth = Math.max(240, startWidth.current + deltaX);
    setSidebarWidth(newWidth);
  };

  const stopResizing = () => {
    document.removeEventListener("mousemove", resize);
    document.removeEventListener("mouseup", stopResizing);
    document.body.classList.remove("decca-resizing");
    if (sidebarRef.current) {
      try {
        localStorage.setItem(
          "decca:sidebarWidth",
          String(sidebarRef.current.getBoundingClientRect().width)
        );
      } catch {}
    }
  };

  const startResizing = (e) => {
    e.preventDefault();
    if (!sidebarRef.current) return;

    document.body.classList.add("decca-resizing");
    startX.current = e.clientX;
    startWidth.current = sidebarRef.current.getBoundingClientRect().width;

    document.addEventListener("mousemove", resize);
    document.addEventListener("mouseup", stopResizing);
  };

  // Cleanup if unmounted during drag
  useEffect(() => {
    return () => stopResizing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Eerste vraag uit het lege scherm (testronde 2026-10-27, B9): het gesprek
  // ontstaat pas bij het eerste bericht, dus een klik op het icoon laat geen
  // leeg gesprek achter.
  const [firstQuestion, setFirstQuestion] = useState("");
  const [pendingFirst, setPendingFirst] = useState(/** @type {{conversationId: string, text: string} | null} */ (null));

  // New conversation
  /** @returns {string} Het ID van het nieuwe gesprek */
  const newConversationHandler = () => {
    const conversationID = uuid();
    const title = `Gesprek over ${currentPage}`;
    const message = `<h2>Hallo ${auth?.ForName || ""}</h2><p>Hoe kan ik je helpen?</p>`;

    // Een nieuw gesprek begint zonder elementcontext. Dat is geen opruiming
    // maar een voorwaarde: de backend leest élk niet-leeg `subject` als "de
    // gebruiker klikte Decca aan op een concreet element" en sluit de router
    // dan kort naar de analyse-agent, zonder ooit te classificeren
    // (functions/decca/orchestrator/router.js, hasButtonContext). Hier stond
    // `setSubject(currentPage)`, waarmee élk nieuw gesprek die kortsluiting
    // aanzette — gemeten 41 van de 42 gesprekken via de balkknop. De
    // paginanaam leverde bovendien niets op: `subjectSwitch` kent alleen
    // analyse-onderwerpen (AIEffectAnalysis, PersonaAnalysis, …) en laat een
    // paginanaam doorvallen naar "Geen specifiek onderwerp".
    //
    // Expliciet wissen, niet overslaan: niets in de app maakt deze drie ooit
    // leeg — sluiten wist alleen tenantOverride en tocContext — dus een
    // onderwerp van een eerdere paginaknop reist anders mee.
    setSubject("");
    setSelectedActivity(null);
    setSelectedEffect(null);
    setContextData("");

    newConversation(
      conversationID,
      title,
      message,
      (conversations?.length || 0) + 1,
      client,
      timestamp,
      uuid(),
      auth?.ID,
      auth?.ForName,
      db
    );

    setSelectedConversationID(conversationID);
    setView("chat");
    return conversationID;
  };

  const startWithQuestion = () => {
    const text = firstQuestion.trim();
    if (!text) return;
    const conversationId = newConversationHandler();
    setPendingFirst({ conversationId, text });
    setFirstQuestion("");
  };

  // Open an existing conversation from the history view
  const openConversationHandler = (conversation) => {
    setSelectedConversationID(conversation.ID);
    setView("chat");
  };

  // Soft-delete a conversation and its messages (ADR-008 — recoverable)
  const deleteConversationHandler = async (conversation) => {
    if (!conversation?.docid) return;

    const deletedFields = {
      Deleted: true,
      DeletedAt: timestamp,
      DeletedBy: auth?.ID || null,
    };

    await db
      .collection("Conversations")
      .doc(conversation.docid)
      .update(deletedFields);

    // Met CompagnyID: Messages is alleen voor het eigen team leesbaar, en een
    // query zonder dat filter weigeren de regels in zijn geheel.
    // AI-CONTEXT: See docs/pitfalls.md#security-open-read-rule-carries-cross-tenant-features
    const snapshot = await db
      .collection("Messages")
      .where("CompagnyID", "==", client)
      .where("ConversationID", "==", conversation.ID)
      .get();

    await Promise.all(snapshot.docs.map((doc) => doc.ref.update(deletedFields)));

    if (selectedConversationID === conversation.ID) {
      setSelectedConversationID(null);
    }
  };

  // IMPORTANT: early return only AFTER all hooks
  if (isHiddenPage) {
    return null;
  }

  return (
    <div id="impactai-sidebar-container">
      {/* Resizer (hover-only visibility via CSS) */}
      <div className="resizer" onMouseDown={startResizing} />

      <div
        id="decca-container"
        ref={sidebarRef}
        className={entered ? "decca-entered" : "decca-enter"}
        style={{ width: `${sidebarWidth}px` }}
      >
        {view === "chat" ? (
          <>
            {/* compact top bar */}
            <div className="dz-toolbar">
              <div className="dz-brand">
                <DeccaAvatar size="md" dot />
                <div className="dz-brand-text">
                  <span className="dz-brand-name">Decca</span>
                  <span className="dz-brand-sub">Impact-adviseur</span>
                </div>
              </div>

              <div className="dz-toolbar-actions">
                <Tooltip content="Gespreksgeschiedenis" left="-120px" top="-50px" width="25px">
                  <button
                    className="dz-ib dz-ib-md"
                    aria-label="Gespreksgeschiedenis"
                    onClick={() => setView("history")}
                  >
                    <DeccaIcon name="history" size={18} />
                  </button>
                </Tooltip>
                <Tooltip content="Nieuw gesprek" left="-100px" top="-50px" width="25px">
                  <button
                    className="dz-ib dz-ib-md"
                    aria-label="Nieuw gesprek"
                    onClick={newConversationHandler}
                  >
                    <DeccaIcon name="compose" size={18} />
                  </button>
                </Tooltip>
                <Tooltip content="Decca sluiten" left="-100px" top="-50px" width="25px">
                  <button
                    className="dz-ib dz-ib-md"
                    aria-label="Decca sluiten"
                    onClick={() => setChatOpen(false)}
                  >
                    <DeccaIcon name="close" size={18} />
                  </button>
                </Tooltip>
              </div>
            </div>

            {selectedConversationID ? (
              <Decca
                conversationId={selectedConversationID}
                initialMessage={pendingFirst?.conversationId === selectedConversationID ? pendingFirst.text : ""}
              />
            ) : (
              <div className="dz-empty-chat">
                <DeccaAvatar size="xl" />
                <div className="dz-empty-chat-form dz-composer-pill">
                  <textarea
                    className="dz-composer-input"
                    rows={1}
                    placeholder="Stel Decca een vraag…"
                    value={firstQuestion}
                    onChange={(e) => setFirstQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        startWithQuestion();
                      }
                    }}
                  />
                  <button
                    className={`dz-ib dz-ib-round dz-ib-send${firstQuestion.trim() ? " dz-ib-send-on" : ""}`}
                    aria-label="Versturen"
                    onClick={startWithQuestion}
                  >
                    <DeccaIcon name="send" size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <History
            conversations={conversations}
            onBack={() => setView("chat")}
            onNewChat={newConversationHandler}
            onOpen={openConversationHandler}
            onDelete={deleteConversationHandler}
          />
        )}
      </div>
    </div>
  );
};

export default Sidebar;
